import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getClubByUser, getClubById } from "../../../services/clubServices";
import {
  getDojosByClub,
  createDojo,
  updateDojo,
  deleteDojo,
} from "../../../services/dojoServices";
import {
  getGroupsByDojo,
  createGroup,
  updateGroup,
  deleteGroup,
  countMembersByGroup,
} from "../../../services/groupServices";
import DojoModal from "./DojoModal";
import GroupModal from "./GroupModal";
import DeleteDojoModal from "./DeleteDojoModal";
import {
  FaArrowRight,
  FaBuilding,
  FaCheckCircle,
  FaEdit,
  FaMapMarkerAlt,
  FaPlus,
  FaSchool,
  FaTrash,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const GROUP_COLOR_MAP = {
  "bg-indigo-400": "bg-indigo-400",
  "bg-indigo-500": "bg-indigo-500",

  "bg-purple-400": "bg-purple-400",
  "bg-purple-500": "bg-purple-500",

  "bg-green-400": "bg-green-400",
  "bg-green-500": "bg-green-500",

  "bg-yellow-400": "bg-yellow-400",
  "bg-yellow-500": "bg-yellow-500",

  "bg-blue-400": "bg-blue-400",
  "bg-blue-500": "bg-blue-500",

  "bg-red-400": "bg-red-400",
  "bg-red-500": "bg-red-500",

  "bg-pink-400": "bg-pink-400",
  "bg-pink-500": "bg-pink-500",

  "bg-gray-400": "bg-gray-400",
  "bg-gray-500": "bg-gray-500",

  "bg-cyan-400": "bg-cyan-400",
  "bg-cyan-500": "bg-cyan-500",

  "bg-orange-400": "bg-orange-400",
  "bg-orange-500": "bg-orange-500",
};

const DojoGroupe = () => {
  useScrollToTop();
  const { addNotification } = useNotification();
  const navigate = useNavigate();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const roleId = Number(userInfo?.role_id || 0);
  const demoMode = roleId === 2;

  const dojoLimitMap = {
    7: 1, // Presence Free
    3: 1, // Presence
    4: 3, // Presence Pro
    5: Infinity, // Presence Ultim
  };

  const groupLimitMap = {
    7: 2, // Presence Free
    3: Infinity,
    4: Infinity,
    5: Infinity,
  };

  const dojoLimit = dojoLimitMap[roleId] ?? Infinity;
  const groupLimit = groupLimitMap[roleId] ?? Infinity;

  const [club, setClub] = useState(null);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState({});
  const [groupMembersCount, setGroupMembersCount] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDojo, setSelectedDojo] = useState(null);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dojoToDelete, setDojoToDelete] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchClub();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchClub = async () => {
    try {
      setLoading(true);

      const data = demoMode
        ? await getClubById(2)
        : await getClubByUser(userId);

      if (data && data.id) {
        setClub(data);
        await fetchDojos(data.id);
      } else {
        setClub(null);
        setDojos([]);
        setGroups({});
      }
    } catch (error) {
      console.error("Erreur lors du chargement du club :", error);
      setClub(null);
      setDojos([]);
      setGroups({});
      addNotification("Erreur lors du chargement du club.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDojos = async (clubId) => {
    if (!clubId) return;

    try {
      const data = await getDojosByClub(clubId);
      const dojoList = Array.isArray(data) ? data : [];
      setDojos(dojoList);

      if (!dojoList.length) {
        setGroups({});
        return;
      }

      await Promise.all(dojoList.map((dojo) => fetchGroups(dojo.id)));
    } catch (error) {
      console.error("Erreur lors du chargement des dojos :", error);
      setDojos([]);
      setGroups({});
      addNotification("Erreur lors du chargement des lieux.", "error");
    }
  };

  const fetchGroups = async (dojoId) => {
    try {
      const data = await getGroupsByDojo(dojoId);
      const dojoGroups = Array.isArray(data) ? data : [];

      setGroups((prevGroups) => ({
        ...prevGroups,
        [dojoId]: dojoGroups,
      }));

      await Promise.all(
        dojoGroups.map((group) => fetchGroupMembersCount(group.id)),
      );
    } catch (error) {
      console.error(
        `Erreur lors du chargement des groupes pour le dojo ${dojoId} :`,
        error,
      );
      setGroups((prevGroups) => ({
        ...prevGroups,
        [dojoId]: [],
      }));
    }
  };

  const fetchGroupMembersCount = async (groupId) => {
    try {
      const count = await countMembersByGroup(groupId);
      setGroupMembersCount((prev) => ({
        ...prev,
        [groupId]: count || 0,
      }));
    } catch (error) {
      console.error(
        `Erreur lors du comptage des membres pour le groupe ${groupId} :`,
        error,
      );
    }
  };

  const totalGroupsCount = useMemo(() => {
    return Object.values(groups).reduce((acc, dojoGroups) => {
      return acc + (Array.isArray(dojoGroups) ? dojoGroups.length : 0);
    }, 0);
  }, [groups]);

  const hasReachedDojoLimit =
    dojoLimit !== Infinity && dojos.length >= dojoLimit;

  const hasReachedGroupLimit =
    groupLimit !== Infinity && totalGroupsCount >= groupLimit;

  const handleCreateOrUpdateDojo = async (dojoData) => {
    if (demoMode) {
      addNotification("Vous ne pouvez pas modifier en mode démo.", "warning");
      return;
    }

    try {
      setSubmitting(true);

      if (editMode && selectedDojo?.id) {
        await updateDojo(selectedDojo.id, dojoData);
        addNotification("Lieu mis à jour avec succès.", "success");
      } else {
        if (hasReachedDojoLimit) {
          addNotification(
            `Vous avez atteint la limite de ${dojoLimit} lieu(x) pour votre formule.`,
            "error",
          );
          return;
        }

        await createDojo({ ...dojoData, club_id: club.id });
        addNotification("Lieu créé avec succès.", "success");
      }

      await fetchDojos(club.id);
      setModalOpen(false);
      setSelectedDojo(null);
    } catch (error) {
      console.error("Erreur lors de la gestion du lieu :", error);
      addNotification("Erreur lors de la mise à jour du lieu.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (dojo) => {
    if (!dojo?.id) return;
    setDojoToDelete(dojo);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDojoToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (demoMode) {
      addNotification("Mode Démo : suppression non autorisée.", "warning");
      return;
    }

    if (!dojoToDelete?.id) {
      addNotification("Erreur : lieu invalide.", "error");
      return;
    }

    try {
      setSubmitting(true);
      await deleteDojo(dojoToDelete.id);
      addNotification("Lieu supprimé avec succès.", "success");
      await fetchDojos(club.id);
    } catch (error) {
      console.error("Erreur lors de la suppression du lieu :", error);
      addNotification("Erreur lors de la suppression du lieu.", "error");
    } finally {
      setSubmitting(false);
      closeDeleteModal();
    }
  };

  const handleCreateOrUpdateGroup = async (groupData) => {
    if (demoMode) {
      addNotification("Vous ne pouvez pas modifier en mode démo.", "warning");
      return;
    }

    if (!selectedDojo?.id) {
      addNotification("Aucun lieu sélectionné.", "error");
      return;
    }

    try {
      setSubmitting(true);

      if (selectedGroup?.id) {
        await updateGroup(selectedGroup.id, groupData);
        addNotification("Groupe mis à jour avec succès.", "success");
      } else {
        if (hasReachedGroupLimit) {
          addNotification(
            `Vous avez atteint la limite de ${groupLimit} groupe(s) pour votre formule.`,
            "error",
          );
          return;
        }

        await createGroup({ ...groupData, dojo_id: selectedDojo.id });
        addNotification("Groupe créé avec succès.", "success");
      }

      await fetchGroups(selectedDojo.id);
      setGroupModalOpen(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Erreur lors de la gestion du groupe :", error);
      addNotification("Erreur lors de la gestion du groupe.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGroup = async (dojoId, groupId) => {
    if (demoMode) {
      addNotification("Mode Démo : suppression non autorisée.", "warning");
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce groupe ?")) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteGroup(groupId);
      addNotification("Groupe supprimé avec succès.", "success");
      await fetchGroups(dojoId);
    } catch (error) {
      console.error("Erreur lors de la suppression du groupe :", error);
      addNotification("Erreur lors de la suppression du groupe.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDojo = () => {
    if (demoMode) {
      addNotification("Mode Démo : ajout désactivé.", "warning");
      return;
    }

    if (hasReachedDojoLimit) {
      addNotification(
        `Vous avez atteint la limite de ${dojoLimit} lieu(x) pour votre formule.`,
        "error",
      );
      return;
    }

    setEditMode(false);
    setSelectedDojo(null);
    setModalOpen(true);
  };

  const openCreateGroup = (dojo) => {
    if (demoMode) {
      addNotification("Mode Démo : ajout désactivé.", "warning");
      return;
    }

    if (hasReachedGroupLimit) {
      addNotification(
        `Vous avez atteint la limite de ${groupLimit} groupe(s) pour votre formule.`,
        "error",
      );
      return;
    }

    setSelectedDojo(dojo);
    setSelectedGroup(null);
    setGroupModalOpen(true);
  };

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
        {/* Header */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8 mt-4">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Votre compte
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Lieux & groupes
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Organisez vos lieux d’activité et vos groupes en un seul espace.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {roleId === 7 && (
                <span className="inline-flex rounded-full bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  Presence Free · 1 lieu / 2 groupes
                </span>
              )}

              {roleId === 3 && (
                <span className="inline-flex rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                  Presence · 1 lieu
                </span>
              )}

              {roleId === 4 && (
                <span className="inline-flex rounded-full bg-indigo-100 px-4 py-2 text-sm font-semibold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Presence Pro · 3 lieux
                </span>
              )}

              {demoMode && (
                <span className="inline-flex rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  Mode Démo
                </span>
              )}

              {club && (
                <button
                  onClick={openCreateDojo}
                  disabled={demoMode || hasReachedDojoLimit}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
                    demoMode || hasReachedDojoLimit
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
                  )}
                >
                  <FaPlus />
                  Ajouter un lieu
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Club summary */}
        <section className="mt-5">
          {loading ? (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto h-24 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
                <div className="mx-auto mt-4 h-5 w-40 rounded bg-slate-200 dark:bg-slate-800" />
              </div>
              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            </div>
          ) : !club ? (
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <FaSchool className="text-4xl" />
              </div>

              <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
                Aucun club trouvé
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Créez d’abord votre club avant d’ajouter vos lieux et vos
                groupes.
              </p>

              <button
                onClick={() => navigate("/votre-club")}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Aller à votre club
                <FaArrowRight />
              </button>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                    Club
                  </p>
                  <h2 className="mt-2 text-2xl font-black">{club.nom_club}</h2>
                  <p className="mt-2 text-sm text-white/80">
                    {club.departement_club || "Département non renseigné"}
                  </p>
                </div>

                <div className="p-6 text-center">
                  {club.logo_url ? (
                    <img
                      src={club.logo_url}
                      alt={club.nom_club}
                      className="mx-auto h-28 w-28 rounded-full border-4 border-white object-cover shadow-xl ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                    />
                  ) : (
                    <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-xl dark:bg-slate-800 dark:text-slate-300">
                      <FaSchool className="text-5xl" />
                    </div>
                  )}

                  <p className="mt-5 text-sm text-slate-600 dark:text-slate-400">
                    {club.adresse_club || "Adresse non renseignée"}
                  </p>
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaBuilding />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Lieux
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {dojos.length}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaUsers />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Groupes
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {totalGroupsCount}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <FaCheckCircle />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Limite actuelle
                    </p>
                    <p className="mt-2 text-lg font-black text-slate-900 dark:text-slate-100">
                      {dojoLimit === Infinity
                        ? "Illimitée"
                        : `${dojoLimit} lieu(x)`}
                    </p>
                  </div>
                </div>

                {(hasReachedDojoLimit || hasReachedGroupLimit) && !demoMode && (
                  <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                    {hasReachedDojoLimit && (
                      <p>
                        Vous avez atteint la limite de lieux autorisée par votre
                        formule.
                      </p>
                    )}
                    {hasReachedGroupLimit && (
                      <p>
                        Vous avez atteint la limite de groupes autorisée par
                        votre formule.
                      </p>
                    )}
                    <a
                      href="/formules-tarifs"
                      className="mt-2 inline-block font-semibold text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Passer à une formule supérieure
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Dojos */}
        {!loading && club && (
          <section className="mt-6">
            {dojos.length > 0 ? (
              <div className="grid gap-5 xl:grid-cols-2">
                {dojos.map((dojo) => {
                  const dojoGroups = groups[dojo.id] || [];

                  return (
                    <article
                      key={dojo.id}
                      className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="border-b border-slate-200 p-5 dark:border-slate-800">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-center gap-4">
                            {dojo.logo_dojo ? (
                              <img
                                src={dojo.logo_dojo}
                                alt={dojo.nom_dojo}
                                className="h-20 w-20 rounded-2xl object-cover shadow-lg"
                              />
                            ) : (
                              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                                <FaBuilding className="text-3xl" />
                              </div>
                            )}

                            <div className="min-w-0">
                              <h3 className="truncate text-2xl font-black text-slate-900 dark:text-slate-100">
                                {dojo.nom_dojo}
                              </h3>
                              <p className="mt-2 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                <FaMapMarkerAlt className="shrink-0" />
                                <span className="truncate">
                                  {dojo.adresse_dojo ||
                                    "Adresse non renseignée"}
                                </span>
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setEditMode(true);
                                setSelectedDojo(dojo);
                                setModalOpen(true);
                              }}
                              className={cn(
                                "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition",
                                demoMode
                                  ? "bg-slate-400 dark:bg-slate-700"
                                  : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
                              )}
                            >
                              <FaEdit />
                              Modifier
                            </button>

                            <button
                              onClick={() => openDeleteModal(dojo)}
                              disabled={demoMode || submitting}
                              className={cn(
                                "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition",
                                demoMode || submitting
                                  ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                                  : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600",
                              )}
                            >
                              <FaTrash />
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="mb-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                              Groupes
                            </p>
                            <h4 className="text-xl font-black text-slate-900 dark:text-slate-100">
                              {dojoGroups.length} groupe(s)
                            </h4>
                          </div>

                          <button
                            onClick={() => openCreateGroup(dojo)}
                            disabled={demoMode || hasReachedGroupLimit}
                            className={cn(
                              "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition",
                              demoMode || hasReachedGroupLimit
                                ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                                : "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600",
                            )}
                          >
                            <FaPlus />
                            Ajouter un groupe
                          </button>
                        </div>

                        {dojoGroups.length > 0 ? (
                          <div className="space-y-3">
                            {dojoGroups
                              .slice()
                              .sort(
                                (a, b) =>
                                  Number(a.ordre_groupe || 0) -
                                  Number(b.ordre_groupe || 0),
                              )
                              .map((groupe) => (
                                <div
                                  key={groupe.id}
                                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60"
                                >
                                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span
                                          className={cn(
                                            "inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide text-white",
                                            GROUP_COLOR_MAP[
                                              groupe.color_groupe
                                            ] || "bg-slate-500",
                                          )}
                                        >
                                          {groupe.nom_groupe}
                                        </span>

                                        <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                                          Ordre {groupe.ordre_groupe || 0}
                                        </span>

                                        <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                                          <FaUserFriends />
                                          {groupMembersCount[groupe.id] ?? 0}
                                        </span>
                                      </div>

                                      {groupe.description_groupe ? (
                                        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
                                          {groupe.description_groupe}
                                        </p>
                                      ) : (
                                        <p className="mt-3 text-sm italic text-slate-400 dark:text-slate-500">
                                          Aucune description
                                        </p>
                                      )}
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        onClick={() => {
                                          setSelectedGroup(groupe);
                                          setSelectedDojo(dojo);
                                          setGroupModalOpen(true);
                                        }}
                                        className={cn(
                                          "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition",
                                          demoMode
                                            ? "bg-slate-400 dark:bg-slate-700"
                                            : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
                                        )}
                                      >
                                        <FaEdit />
                                        Éditer
                                      </button>

                                      <button
                                        onClick={() =>
                                          handleDeleteGroup(dojo.id, groupe.id)
                                        }
                                        disabled={demoMode || submitting}
                                        className={cn(
                                          "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition",
                                          demoMode || submitting
                                            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                                            : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600",
                                        )}
                                      >
                                        <FaTrash />
                                        Supprimer
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-800/40">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Aucun groupe pour ce lieu.
                            </p>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  <FaBuilding className="text-4xl" />
                </div>

                <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
                  Aucun lieu enregistré
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                  Ajoutez votre premier lieu pour structurer vos groupes.
                </p>

                <button
                  onClick={openCreateDojo}
                  disabled={demoMode || hasReachedDojoLimit}
                  className={cn(
                    "mt-6 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    demoMode || hasReachedDojoLimit
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
                  )}
                >
                  <FaPlus />
                  Ajouter un lieu
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      {modalOpen && (
        <DojoModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreateOrUpdateDojo}
          dojo={selectedDojo}
          demoMode={demoMode}
          isSubmitting={submitting}
        />
      )}

      {groupModalOpen && (
        <GroupModal
          isOpen={groupModalOpen}
          onClose={() => setGroupModalOpen(false)}
          onSubmit={handleCreateOrUpdateGroup}
          group={selectedGroup}
          dojoId={selectedDojo?.id}
          dojoName={selectedDojo?.nom_dojo}
          demoMode={demoMode}
          isSubmitting={submitting}
        />
      )}

      <DeleteDojoModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        dojoName={dojoToDelete?.nom_dojo}
        onConfirm={handleConfirmDelete}
        isSubmitting={submitting}
      />
    </div>
  );
};

export default DojoGroupe;
